import { EventInput } from "@fullcalendar/core";
import { addDays, endOfDay, format, startOfDay } from "date-fns";

import { TDatesFilter, TDurationResult, TTask } from "@/types";

import { getMonthRange } from "./UDates";

/**
 * The home filter period that shows `task`: its own days when it has
 * both dates, else the month it starts in. What the search hits and the
 * project page set before navigating to /tasks/:id - the home page reads
 * the task from the app state, not from the URL.
 */
export const periodOfTask = (
	task: Pick<TTask, "startDate" | "endDate">
): TDatesFilter =>
	task.startDate && task.endDate
		? {
				dateFrom: format(new Date(task.startDate), "yyyy-MM-dd"),
				dateTo: format(new Date(task.endDate), "yyyy-MM-dd"),
			}
		: getMonthRange(new Date(task.startDate));

export const taskToCalendarEvents = (task: TTask): EventInput[] => {
	const events: EventInput[] = [];
	const startDate = new Date(task.startDate);
	const endDate = task.endDate
		? new Date(task.endDate)
		: startDate.getTime() > new Date().getTime() // handle future tasks display
			? startDate
			: new Date();

	if (endDate && startDate.toDateString() === endDate.toDateString()) {
		// Task does not span multiple days or has no EndDate
		events.push({
			title: task.name,
			start: startDate,
			end:
				endDate ||
				new Date(format(startDate, "yyyy-MM-dd") + "T23:59:59"),
			color: task.project?.color,
			extendedProps: task,
			defaultTimedEventDuration: "00:01",
			forceEventDuration: true,
		});
	} else {
		// Task spans multiple days
		let currentStart = startDate;

		while (currentStart < endDate) {
			const isLastDay =
				addDays(currentStart, 1).toDateString() ===
				endDate.toDateString();

			events.push({
				title: task.name,
				start: currentStart,
				end: isLastDay ? endDate : endOfDay(currentStart),
				color: task.project?.color,
				extendedProps: task,
				defaultTimedEventDuration: "00:01",
				forceEventDuration: true,
			});
			currentStart = startOfDay(addDays(currentStart, 1));
		}
	}

	return events;
};

export const tasksToCalendarEvents = (tasks: TTask[]): EventInput[] => {
	if (!Array.isArray(tasks) || !tasks?.length) return [];
	return tasks.flatMap(taskToCalendarEvents);
};

/** A day's tasks at a glance: how many, and how they split by project. */
export type TDaySummary = {
	count: number;
	/** One per project colour, the biggest share first. */
	segments: { color: string; count: number }[];
};

/** A task without a project, in a summary bar. */
export const NO_PROJECT_COLOR = "#d4d4d4";

/**
 * One all-day event per day in place of one per task: the compact
 * calendar draws each as a bar (`extendedProps.summary`). Takes the
 * events `tasksToCalendarEvents` made, so a task over several days
 * counts on each of them.
 */
export const summarizeEventsByDay = (events: EventInput[]): EventInput[] => {
	const days = new Map<string, Map<string, number>>();
	events.forEach((event) => {
		if (!event.start) return;
		const day = format(new Date(event.start as Date), "yyyy-MM-dd");
		const color = (event.color as string | undefined) || NO_PROJECT_COLOR;
		const colors = days.get(day) ?? new Map<string, number>();
		colors.set(color, (colors.get(color) ?? 0) + 1);
		days.set(day, colors);
	});

	return Array.from(days, ([day, colors]) => {
		const segments = Array.from(colors, ([color, count]) => ({
			color,
			count,
		})).sort((a, b) => b.count - a.count);
		const summary: TDaySummary = {
			count: segments.reduce((sum, { count }) => sum + count, 0),
			segments,
		};
		return {
			start: day,
			allDay: true,
			backgroundColor: "transparent",
			borderColor: "transparent",
			extendedProps: { summary },
		};
	});
};

export const orderTasksByDate = (tasks: TTask[]) => {
	if (!Array.isArray(tasks) || !tasks?.length) return [];
	const Days = tasks.reduce(
		(acc, task) => {
			const date = format(new Date(task.startDate), "yyyy-MM-dd");
			if (!acc[date]) acc[date] = [];
			acc[date].push(task);
			return acc;
		},
		{} as Record<string, TTask[]>
	);

	return Object.entries(Days).map(([date, tasks]) => ({
		date,
		tasks: tasks.sort((a, b) => {
			const dateA = new Date(a.startDate).getTime();
			const dateB = new Date(b.startDate).getTime();
			return dateA - dateB;
		}),
	}));
};

export const calculateDuration = (tasks: TTask[]): TDurationResult => {
	if (tasks.length === 0) {
		return { duration: "0h0", dateFrom: null };
	}

	// Convertir les dates en objets Date et trier les tâches par date de début
	const sortedTasks = tasks
		.map((task) => ({
			start: new Date(task.startDate),
			end: task.endDate ? new Date(task.endDate) : null,
		}))
		.sort((a, b) => a.start.getTime() - b.start.getTime());

	let totalMinutes = 0;
	let currentEnd = sortedTasks[0].start;
	let ongoingTask = false;

	sortedTasks.forEach((task) => {
		const taskEnd = task.end || new Date();

		if (!task.end) {
			ongoingTask = true;
		}

		if (task.start >= currentEnd) {
			// Si la tâche ne chevauche pas, ajouter la durée de la tâche
			totalMinutes +=
				(taskEnd.getTime() - task.start.getTime()) / 1000 / 60;
			currentEnd = taskEnd;
		} else if (taskEnd > currentEnd) {
			// Si la tâche chevauche, ajouter seulement la différence entre currentEnd et taskEnd
			totalMinutes +=
				(taskEnd.getTime() - currentEnd.getTime()) / 1000 / 60;
			currentEnd = taskEnd;
		}
	});

	const hours = Math.floor(totalMinutes / 60)
		.toString()
		.padStart(2, "0");
	const minutes = Math.floor(totalMinutes % 60)
		.toString()
		.padStart(2, "0");
	const duration = `${hours}h${minutes}`;

	// Calculer la date de départ si une tâche est en cours
	const dateFrom = ongoingTask
		? new Date(new Date().getTime() - totalMinutes * 60 * 1000)
		: null;

	return { duration, dateFrom };
};

export const mergeTasks = <T extends Pick<TTask, "name" | "endDate">>(
	_tasks: T[] = []
): T[] => {
	if (!Array.isArray(_tasks) || !_tasks.length) return [];
	const tasks = _tasks.reduce((acc, task) => {
		const existingTask = acc.find((t) => t.name === task.name);

		if (existingTask) {
			existingTask.endDate = task.endDate || existingTask.endDate;
		} else {
			acc.push({ ...task });
		}

		return acc;
	}, [] as T[]);

	return tasks;
};

export default calculateDuration;
